/**
 * Formats a Date to RRule format without timezone (YYYYMMDDTHHMMSS)
 * This ensures events maintain wall-clock time across DST transitions.
 *
 * @param date - The date to format
 * @returns Formatted date string in RRule format (local time, no timezone)
 */
export function formatLocalDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Parameters for building an RRule string
 */
export interface RRuleParams {
  dtstart: string;
  freq: number;
  interval?: number | null;
  byweekday?: number[] | null;
  bysetpos?: number | null;
  bymonthday?: number | null;
  until?: string | null;
}

/**
 * Builds RRule string in local time (no timezone info)
 * This prevents DST-related time shifts by using wall-clock time.
 *
 * @param params - RRule parameters
 * @returns RRule string formatted for FullCalendar
 */
export function buildRRuleString(params: RRuleParams): string {
  const freqMap: Record<number, string> = {
    0: 'YEARLY',
    1: 'MONTHLY',
    2: 'WEEKLY',
    3: 'DAILY',
    4: 'HOURLY',
    5: 'MINUTELY',
    6: 'SECONDLY',
  };

  let rrule = `DTSTART:${params.dtstart}\nRRULE:FREQ=${freqMap[params.freq] || 'DAILY'}`;

  if (params.interval && params.interval > 1) {
    rrule += `;INTERVAL=${params.interval}`;
  }

  // bymonthday and byweekday+bysetpos are mutually exclusive for MONTHLY.
  // When bymonthday is set, skip BYDAY to avoid "27th that is also a Friday" semantics.
  const isMonthlyByMonthday = params.freq === 1 && params.bymonthday != null;

  if (!isMonthlyByMonthday && params.byweekday && params.byweekday.length > 0) {
    const dayMap = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'];
    const days = params.byweekday.map((d) => dayMap[d]).join(',');
    rrule += `;BYDAY=${days}`;
  }

  if (!isMonthlyByMonthday && params.bysetpos != null) {
    rrule += `;BYSETPOS=${params.bysetpos}`;
  }

  if (params.bymonthday != null) {
    rrule += `;BYMONTHDAY=${params.bymonthday}`;
  }

  if (params.until) {
    rrule += `;UNTIL=${params.until}`;
  }

  return rrule;
}

/**
 * Event duration represented as hours and minutes
 */
export interface EventDuration {
  hours: number;
  minutes: number;
}

/**
 * Calculates duration as hour/minute object for DST-safe event duration.
 * Using time-based duration (hours/minutes) instead of absolute milliseconds
 * ensures events maintain consistent duration across DST transitions.
 *
 * @param startDate - Event start date
 * @param endDate - Event end date
 * @returns Duration as hours and minutes
 */
export function calculateDuration(startDate: Date, endDate: Date): EventDuration {
  const durationInMs = endDate.getTime() - startDate.getTime();
  return {
    hours: Math.floor(durationInMs / 3600000),
    minutes: Math.floor((durationInMs % 3600000) / 60000),
  };
}
